import { accessControlTabs, devicesTab } from 'utils/constants';
import { ADMINISTRATION } from 'utils/route-constants';
import React from 'react';
import { Box } from '@mui/material';

export function firestoreTimestampToDate(timestamp) {
    if (!timestamp?.seconds) return null;

    return new Date(
        timestamp.seconds * 1000 + Math.floor((timestamp.nanoseconds || 0) / 1000000)
    );
}

export function formatNotificationTime(createdAt) {
    const date = firestoreTimestampToDate(createdAt);

    if (!date) return '';

    const now = new Date();

    const isSameDay = (a, b) =>
        a.getFullYear() === b.getFullYear() &&
        a.getMonth() === b.getMonth() &&
        a.getDate() === b.getDate();

    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);

    const time = date.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
    });

    if (isSameDay(date, now)) {
        return `Today, ${time}`;
    }

    if (isSameDay(date, yesterday)) {
        return `Yesterday, ${time}`;
    }

    const formattedDate = date.toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric'
    });

    return `${formattedDate}, ${time}`;
}

export function getInitialsFromText(text = '') {
    const words = text.replace(/[“”"]/g, '').split(' ').filter(Boolean);

    if (words.length < 2) return words[0]?.slice(0, 2).toUpperCase() || '?';

    return `${words[0][0]}${words[1][0]}`.toUpperCase();
}

export function isAccessNotification(notification) {
    return (
        notification?.template_key === 'access-granted-vendor' ||
        notification?.template_key === 'otc-consumed' ||
        notification?.template_key === 'access-granted-kiosk' ||
        notification?.template_key === 'access-granted'
    );
}

/**
 * The manager-side call to action for a notification, or null when the row is purely informational.
 *
 * Only manager notifications carry one today — resident templates all return null, so the resident
 * drawer renders exactly as before. `to` is a router path; the drawer opens it and closes itself.
 *
 * Every action carries the org it belongs to (`orgId` / `orgName`, from the notification): the page
 * is the operator app's, and only that org can show it, so acting on it first switches there (see
 * `hooks/useNotificationAction.js`).
 */
export function getNotificationAction(notification) {
    const action = getActionTarget(notification);

    if (!action) return null;

    return {
        ...action,
        orgId: notification?.org_id || null,
        orgName: notification?.org_name || ''
    };
}

function getActionTarget(notification) {
    switch (notification?.template_key) {
        // The Requests tab lives on the Administration page (`/administration` → AccessControl.jsx);
        // `/access-control` is the access-logs page and ignores `?tab=`.
        case 'invite-pending-approval':
            return {
                label: 'Review pending invite',
                to: `${ADMINISTRATION}?tab=${encodeURIComponent(
                    accessControlTabs.REQUESTS
                )}`
            };
        case 'credential-grants-pending':
            return {
                label: 'Review pending grants',
                to: `/devices?tab=${encodeURIComponent(
                    devicesTab.PENDING_DEVICES
                )}&source=shared`
            };
        case 'credential-grants-holder-removed': {
            const personName = notification?.data?.person_name;
            const search = personName ? `&q=${encodeURIComponent(personName)}` : '';

            return {
                label: 'Review shared credentials',
                to: `/devices?tab=${encodeURIComponent(
                    devicesTab.ACTIVE_DEVICES
                )}&source=shared${search}`
            };
        }
        // Access already ended (the badge is gone and the grants are already revoked) — there is
        // nothing left to act on, so this one stays unclickable, unlike holder-removed above.
        case 'credential-grants-access-ended':
            return null;
        // Consumer-issued devices (CONSUMER-ISSUED-CREDENTIALS-V2.md §4/§6) — a NEW notifier,
        // distinct from the credential-grants ones above (own tab, own permission). All four land
        // on the Administration page's Shared Members tab, pre-filtered to the sponsor the notification
        // names; "person left" also pre-filters to the flagged rows it just affected.
        case 'consumer-device-person-left':
            return sharedMembersAction(notification, { flagged: true });
        case 'consumer-device-agreement-ended':
            return sharedMembersAction(notification);
        case 'consumer-device-sharing-paused':
            return sharedMembersAction(notification);
        // The bulk expiry job just finished — nothing left to review beyond the counts already in
        // the notification text, so this one stays unclickable like access-ended above.
        case 'consumer-device-bulk-expired':
            return null;
        default:
            return null;
    }
}

// Notifications are uid-scoped across every org the manager administers. The drawer switches to the
// action's org before opening the page; the `org` param in the link stays so the Administration page
// still warns when the active org differs (see `AccessControl.jsx`).
function sharedMembersAction(notification, { flagged = false } = {}) {
    return {
        label: 'Review shared devices',
        to: sharedMembersDeepLink(notification, { flagged })
    };
}

function sharedMembersDeepLink(notification, { flagged = false } = {}) {
    const sponsorId = notification?.data?.sponsoring_org_id;
    const params = new URLSearchParams({ tab: accessControlTabs.SHARED_MEMBERS });
    if (notification?.org_id) params.set('org', notification.org_id);
    if (sponsorId) params.set('sponsor', sponsorId);
    if (flagged) params.set('flagged', '1');
    return `${ADMINISTRATION}?${params.toString()}`;
}

export function formatNotificationText(notification) {
    const { title, text, subtexts = [] } = notification;

    const highlightedText = highlightSubtexts(text, subtexts);

    if (!title) {
        return highlightedText;
    }

    // Older titles end in "!" or "?" ("You've been invited!"), which must not get a second mark.
    const titleEnd = /[.!?]$/.test(title) ? '' : '.';

    return (
        <>
            <Box component="span" sx={{ fontWeight: 600 }}>
                {title}
                {titleEnd}
            </Box>{' '}
            {highlightedText}
        </>
    );
}
export function highlightSubtexts(text = '', subtexts = []) {
    if (!subtexts.length) {
        return text;
    }

    const escapedSubtexts = subtexts
        .filter(Boolean)
        .sort((a, b) => b.length - a.length)
        .map(value => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));

    const regex = new RegExp(`(${escapedSubtexts.join('|')})`, 'gi');

    return text.split(regex).map((part, index) => {
        const isMatch = subtexts.some(
            subtext => subtext.toLowerCase() === part.toLowerCase()
        );

        if (isMatch) {
            return (
                <Box key={index} component="span" sx={{ fontWeight: 600 }}>
                    {part}
                </Box>
            );
        }

        return part;
    });
}
